import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { FadeUp } from "@/components/Reveal";
import { PAGES } from "@/App";

const COVERS = [
    { src: "/img/davido-concert-photo-2.jpg", depth: 0.35, className: "left-[6%] top-[14%] w-28 rotate-[-6deg] md:w-44" },
    { src: "/img/davido-concert-photo-1.jpg", depth: 0.2, className: "right-[8%] top-[10%] w-24 rotate-[5deg] md:w-40" },
    { src: "/img/davido-concert-photo-3.jpg", depth: 0.5, className: "bottom-[10%] right-[14%] w-24 rotate-[-4deg] md:w-36" },
];

export default function PageFooter({ page, onNav }) {
    const { lang } = useLang();
    const [progress, setProgress] = useState(0);
    const wrapRef = useRef(null);

    const idx = PAGES.findIndex((p) => p.id === page);
    const next = PAGES[(idx + 1) % PAGES.length];

    useEffect(() => {
        let raf;
        const onScroll = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const max = document.documentElement.scrollHeight - window.innerHeight;
                setProgress(max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);
            });
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("scroll", onScroll);
        };
    }, [page]);

    return (
        <footer
            ref={wrapRef}
            data-testid="page-footer"
            className="relative border-t border-white/10 bg-[#101014] px-5 pb-12 pt-20 md:px-10 md:pt-28"
        >
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.12]"
                style={{
                    background:
                        "radial-gradient(60% 60% at 20% 20%, rgba(138,43,226,0.5), transparent 60%), radial-gradient(50% 50% at 85% 75%, rgba(212,175,55,0.4), transparent 60%)",
                }}
            />
            {/* floating parallax covers */}
            {COVERS.map((c, i) => (
                <motion.img
                    key={i}
                    src={c.src}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    className={`pointer-events-none absolute hidden opacity-40 shadow-[0_20px_60px_rgba(0,0,0,0.6)] md:block ${c.className}`}
                    style={{ y: (progress - 0.5) * 220 * c.depth }}
                />
            ))}
            <div className="relative mx-auto max-w-[1600px]">
                <FadeUp>
                    <p className="font-mono text-[10px] tracking-[0.35em] text-cream/40">
                        {tx(lang, T.pagenav.next)}
                    </p>
                    <button
                        data-testid="page-next-btn"
                        onClick={() => onNav(next.id)}
                        className="group mt-4 flex w-full items-center justify-between border-t border-white/10 pt-8 text-left"
                    >
                        <span className="font-display text-[clamp(2.4rem,8vw,7rem)] font-extrabold uppercase leading-none tracking-[-0.02em] text-cream transition-colors duration-500 group-hover:text-violet">
                            {tx(lang, next.label)}
                            <span className="text-violet">.</span>
                        </span>
                        <span className="font-mono text-xs tracking-[0.3em] text-cream/40 transition-colors duration-500 group-hover:text-gold">
                            {next.num} / 04 →
                        </span>
                    </button>
                </FadeUp>
                <div className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-white/10 pt-6 md:flex-row md:items-center">
                    <p className="font-display text-sm font-extrabold uppercase tracking-[0.18em] text-cream">
                        GLOOM ENT.<span className="text-violet">®</span>
                    </p>
                    <div className="flex gap-8">
                        {PAGES.map((p) => (
                            <button
                                key={p.id}
                                data-testid={`footer-nav-${p.id}`}
                                onClick={() => onNav(p.id)}
                                className={`font-mono text-[10px] tracking-[0.3em] transition-colors ${
                                    page === p.id ? "text-violet" : "text-cream/50 hover:text-cream"
                                }`}
                            >
                                {tx(lang, p.label)}
                            </button>
                        ))}
                    </div>
                    <p className="font-mono text-[9px] tracking-[0.3em] text-cream/30">
                        © 2026 — GENEVA, SWITZERLAND
                    </p>
                </div>
            </div>
        </footer>
    );
}
